import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type OrderStatus, type OrderTag } from '../stores/useOrderStore';

interface UrlFilters {
  search: string;
  dateRange: { start: string; end: string };
  statuses: OrderStatus[];
  tags: OrderTag[];
}

interface UrlPagination {
  page: number;
  pageSize: number;
}

interface UrlState {
  filters: UrlFilters;
  pagination: UrlPagination;
}

// Base64 인코딩/디코딩 유틸리티
const encodeToBase64 = (obj: any): string => {
  try {
    const jsonStr = JSON.stringify(obj);
    const encodedStr = encodeURIComponent(jsonStr);
    return btoa(encodedStr);
  } catch (error) {
    console.error('Encoding error:', error);
    return '';
  }
};

const decodeFromBase64 = (str: string): any => {
  try {
    const decodedStr = atob(str);
    const jsonStr = decodeURIComponent(decodedStr);
    return JSON.parse(jsonStr);
  } catch (error) {
    console.error('Decoding error:', error);
    return null;
  }
};

// 기본값
const defaultFilters: UrlFilters = {
  search: '',
  dateRange: { start: '', end: '' },
  statuses: [],
  tags: [],
};



export const useUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL에서 상태 읽기
  const urlState = useMemo((): UrlState => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    const pageSize = parseInt(searchParams.get('pageSize') || '10', 10);
    
    // 필터 정보는 base64로 인코딩되어 저장
    const filtersParam = searchParams.get('filters');
    let filters = defaultFilters;
    
    if (filtersParam) {
      const decodedFilters = decodeFromBase64(filtersParam);
      if (decodedFilters) {
        filters = {
          search: decodedFilters.search || '',
          dateRange: {
            start: decodedFilters.dateRange?.start || '',
            end: decodedFilters.dateRange?.end || '',
          },
          statuses: Array.isArray(decodedFilters.statuses) ? decodedFilters.statuses : [],
          tags: Array.isArray(decodedFilters.tags) ? decodedFilters.tags : [],
        };
      }
    }

    return {
      filters,
      pagination: {
        page: Math.max(1, page),
        pageSize: [5, 10, 20, 50, 100].includes(pageSize) ? pageSize : 10,
      },
    };
  }, [searchParams]);

  // URL 업데이트 함수
  const updateUrl = useCallback((newState: Partial<UrlState>) => {
    const currentState = urlState;
    const updatedState = {
      ...currentState,
      ...newState,
      filters: newState.filters ? { ...currentState.filters, ...newState.filters } : currentState.filters,
      pagination: newState.pagination ? { ...currentState.pagination, ...newState.pagination } : currentState.pagination,
    };

    const newParams = new URLSearchParams();
    
    // 페이지네이션 파라미터
    if (updatedState.pagination.page !== 1) {
      newParams.set('page', updatedState.pagination.page.toString());
    }
    if (updatedState.pagination.pageSize !== 10) {
      newParams.set('pageSize', updatedState.pagination.pageSize.toString());
    }

    // 필터 파라미터 (활성 필터가 있을 때만)
    const hasActiveFilters = 
      updatedState.filters.search ||
      updatedState.filters.dateRange.start ||
      updatedState.filters.dateRange.end ||
      updatedState.filters.statuses.length > 0 ||
      updatedState.filters.tags.length > 0;

    if (hasActiveFilters) {
      const filtersBase64 = encodeToBase64(updatedState.filters);
      if (filtersBase64) {
        newParams.set('filters', filtersBase64);
      }
    }

    setSearchParams(newParams, { replace: true });
  }, [urlState, setSearchParams]);

  // 개별 업데이트 함수들
  const updatePage = useCallback((page: number) => {
    updateUrl({ pagination: { ...urlState.pagination, page } });
  }, [updateUrl, urlState.pagination]);

  const updatePageSize = useCallback((pageSize: number) => {
    updateUrl({ pagination: { page: 1, pageSize } }); // 페이지 크기 변경시 1페이지로 리셋
  }, [updateUrl]);

  const updateFilters = useCallback((filters: Partial<UrlFilters>) => {
    const newFilters = { ...urlState.filters, ...filters };
    updateUrl({ 
      filters: newFilters,
      pagination: { ...urlState.pagination, page: 1 } // 필터 변경시 1페이지로 리셋
    });
  }, [updateUrl, urlState.filters, urlState.pagination]);

  const resetFilters = useCallback(() => {
    updateUrl({ 
      filters: defaultFilters,
      pagination: { ...urlState.pagination, page: 1 }
    });
  }, [updateUrl, urlState.pagination]);

  return {
    // 현재 상태
    filters: urlState.filters,
    pagination: urlState.pagination,
    
    // 업데이트 함수들
    updatePage,
    updatePageSize,
    updateFilters,
    resetFilters,
    updateUrl,
  };
};
